import { supabase } from '../lib/supabase';

export const authService = {
  /**
   * Sign up new user with role-based profile data
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.fullName - User full name
   * @param {string} userData.phone - User phone (optional)
   * @param {string} userData.userType - User role (jobseeker/employer)
   * @param {string} userData.location - User location
   * @param {string} userData.experienceLevel - Experience level (jobseeker only)
   * @param {string} userData.companyName - Company name (employer only)
   */
  async signUp(userData) {
    try {
      const { email, password, fullName, phone, userType, location, experienceLevel, companyName } = userData;

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            phone: phone || '',
            role: userType,
            location,
            experience_level: experienceLevel || '',
            company_name: companyName || ''
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in existing user
   * @param {string} email - User email
   * @param {string} password - User password
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get current user session
   */
  async getCurrentSession() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      if (error) throw error;
      return { session, error: null };
    } catch (error) {
      return { session: null, error };
    }
  },

  /**
   * Get user profile with role information
   * @param {string} userId - User ID
   */
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      if (data) {
        return {
          data: {
            id: data?.id,
            email: data?.email,
            fullName: data?.full_name,
            phone: data?.phone,
            role: data?.role,
            location: data?.location,
            companyName: data?.company_name,
            experienceLevel: data?.experience_level,
            createdAt: data?.created_at,
            updatedAt: data?.updated_at
          },
          error: null
        };
      }

      return { data: null, error: null };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object} updates - Profile updates (camelCase)
   */
  async updateUserProfile(userId, updates) {
    try {
      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
      if (updates?.phone !== undefined) dbUpdates.phone = updates?.phone;
      if (updates?.location !== undefined) dbUpdates.location = updates?.location;
      if (updates?.experienceLevel !== undefined) dbUpdates.experience_level = updates?.experienceLevel;
      if (updates?.companyName !== undefined) dbUpdates.company_name = updates?.companyName;

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        data: data ? {
          id: data?.id,
          email: data?.email,
          fullName: data?.full_name,
          phone: data?.phone,
          role: data?.role,
          location: data?.location,
          experienceLevel: data?.experience_level,
          companyName: data?.company_name,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        } : null,
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};